import { createQueryKeys } from '@lukemorales/query-key-factory'
import { useMutation, useQuery } from 'react-query'
import queryClient from '~/data/queryClient'
import { useIsFeatureEnabled } from '~/data/useData'
import { useAuth } from '~/hooks/useAuth'
import {
  createConversation,
  getBasicStats,
  getChartsData,
  getConversation,
  getFindingsStats,
  getIsZehEnabled,
  getTechnicalStats,
  getUnifiedStats,
  IConversation,
  IFindingsFilters,
  ISendMessageParams,
  ITechnicalByCategory,
  IZehEnabledResult,
  listConversations,
  sendMessage,
} from './zehApi'

/**
 * Base key for zeh-related queries
 */
export const zehBaseKey = 'zeh'

/**
 * Query keys for zeh module
 */
export const zehKeys = createQueryKeys(zehBaseKey, {
  conversations: () => ({
    queryKey: [zehBaseKey, 'conversations'],
  }),
  detail: (id: string) => ({
    queryKey: [zehBaseKey, 'conversations', id],
  }),
  status: (sessionId: string) => ({
    queryKey: [zehBaseKey, 'status', sessionId],
  }),
  enabled: {
    queryKey: [zehBaseKey, 'enabled'],
  },
})

export const useIsZehEnabled = () => {
  return useQuery<IZehEnabledResult>({
    queryKey: zehKeys.enabled.queryKey,
    queryFn: getIsZehEnabled,
    staleTime: 60 * 60, // 1 hour
  })
}

export const useIsZehAnalyticsV2Enabled = () => {
  return useIsFeatureEnabled({
    feature_flag: 'zeh_analytics_v2',
  })
}

export const useIsZehGenieEnabled = () => {
  return useIsFeatureEnabled({
    feature_flag: 'zeh_use_genie',
  })
}

/**
 * Hook for getting a list of conversations
 * @returns Query object with conversations data
 */
export const useListConversations = () => {
  return useQuery({
    queryKey: zehKeys.conversations().queryKey,
    queryFn: listConversations,
  })
}

/**
 * Hook for getting a conversation by ID
 * @param id - The conversation session ID
 * @returns Query object with conversation data
 */
export const useGetConversation = (id: string) => {
  return useQuery<IConversation>({
    queryKey: zehKeys.detail(id).queryKey,
    queryFn: () => getConversation(id),
    enabled: !!id,
    retry: (failureCount, error: any) => {
      if (error.response?.status === 401 || error.response?.status === 403) {
        return false
      }
      return failureCount < 3
    },
    onError: (error) => {
      console.error(`Error fetching conversation ${id}:`, error)
    },
    refetchInterval: 4000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })
}

/**
 * Hook for creating a new conversation
 * @returns Mutation object for creating conversations
 */
export const useCreateConversation = () => {
  return useMutation({
    mutationFn: (userInput: string) => createConversation(userInput),
    onSuccess: () => {
      queryClient.invalidateQueries(zehKeys.conversations().queryKey)
    },
    onError: (error) => {
      console.error('Error creating conversation:', error)
    },
  })
}

/**
 * Hook for sending a message
 * @returns Mutation object for sending messages
 */
export const useSendMessage = () => {
  return useMutation({
    mutationFn: (params: ISendMessageParams) => sendMessage(params),
  })
}

/**
 * Hook for getting chart data
 * @returns Query object for chart data
 */
export const useGetChartData = () => {
  const { data, isLoading } = useQuery({
    queryKey: 'zeh-charts-data',
    queryFn: () => getChartsData(),
    staleTime: 1000 * 60 * 5,
  })

  return {
    data,
    isLoading,
  }
}

/**
 * Unified hook for getting all stats data with filters
 * This makes only ONE API call and shares data across all components
 * @param filters - Optional filters for the findings data
 * @returns Query object with all stats data
 */
export const useGetUnifiedStats = (filters?: IFindingsFilters) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['zeh-unified-stats', filters],
    queryFn: () => getUnifiedStats(filters),
    staleTime: 1000 * 60 * 5,
  })

  return {
    data,
    isLoading,
    error,
    typeClassifications: data?.type_classifications,
    findingsByType: data?.findings_by_type,
    findingsBySeverity: data?.findings_by_severity,
    findingsByLocation: data?.findings_by_location,
  }
}

/**
 * Hook for getting type classifications data
 * @returns Query object for type classifications data
 */
export const useGetTypeClassifications = (filters?: IFindingsFilters) => {
  const { typeClassifications, isLoading, error } = useGetUnifiedStats(filters)

  return {
    data: typeClassifications,
    isLoading,
    error,
  }
}

/**
 * Hook for getting basic stats data (for Audit tab)
 * @returns Query object for basic stats data
 */
export const useGetBasicStats = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['zeh-basic-stats'],
    queryFn: getBasicStats,
    staleTime: 1000 * 60 * 5,
  })

  return {
    data,
    isLoading,
    error,
  }
}

/**
 * Hook for getting findings stats data (for Findings tab)
 * @param filters - Optional filters for the findings data
 * @param enabled - Whether the query should be enabled
 * @returns Query object for findings stats data
 */
export const useGetFindingsStats = (filters?: IFindingsFilters, enabled = true) => {
  const { userId } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['zeh-findings-stats', userId, filters],
    queryFn: () => getFindingsStats(filters),
    staleTime: Infinity,
    enabled,
  })

  return {
    data,
    isLoading,
    error,
    findingsByType: data?.findings_by_type,
    findingsBySeverity: data?.findings_by_severity,
    findingsByLocation: data?.findings_by_location,
  }
}

/**
 * Hook for getting technical stats data (for Technical tab)
 * @param filters - Optional filters for the technical data
 * @param enabled - Whether the query should be enabled
 * @returns Query object for technical stats data
 */
export const useGetTechnicalStats = (filters?: IFindingsFilters, enabled = true) => {
  const { userId } = useAuth()
  const filtersString = JSON.stringify(filters)

  const { data, isLoading, error } = useQuery<ITechnicalByCategory>({
    queryKey: ['zeh-technical-stats', userId, filtersString],
    queryFn: () => getTechnicalStats(filters),
    staleTime: Infinity,
    enabled,
  })

  return {
    data,
    isLoading,
    error,
  }
}
